import React, { useId } from "react";
import { AbsoluteFill, Img, useVideoConfig } from "remotion";
import { meshFields, type Background, type BackgroundPattern, type MeshLayout } from "@veyso/core";
import { useAssetUrl } from "../assets.js";

/**
 * Every pattern, as a flat fill plus the shapes drawn over it.
 *
 * One definition, used by the renderer and by the editor's picker, so a
 * thumbnail cannot promise something the render will not draw.
 *
 * The curved ones are oversized ellipses cropped by the frame — an arc is a
 * circle you can only see part of, and that is far cheaper than path data
 * nobody can read or adjust. Textures take their lines from `from` at reduced
 * opacity rather than from white, so they hold up on a pale background as well
 * as a dark one.
 */
/**
 * Two ways of drawing, and the split between them is not stylistic.
 *
 * There are two renderers. The worker runs headless Chrome and screenshots it,
 * so every CSS feature that exists works. The browser's exporter cannot ask
 * Chrome for its painted output — no browser API hands live DOM to a canvas —
 * so `@remotion/web-renderer` re-implements CSS painting with Canvas 2D calls,
 * and it only implements what somebody has written. `background-image` is
 * drawn only when it parses as a `linear-gradient`; every other value is
 * dropped, and an element whose gradient was its only fill paints *nothing*.
 * That is how a Glow background exported as a black frame.
 *
 * So `base` and `layers` are CSS, and carry the solid fills, the linear
 * gradients and the plain-coloured ellipses — all of which both renderers draw
 * identically. `svg` carries everything else: radial gradients and repeats.
 * SVG is not subject to the same gap, because the exporter does not
 * re-implement it either — it serialises the element to a data URL and hands
 * it to `new Image()`, which means *Chrome* rasterises it, the same as in the
 * preview and on the worker.
 *
 * The rule for anything added later: if it is a colour or a `linear-gradient`,
 * CSS is fine. Anything else belongs in `svg`, or it will quietly not appear.
 */
interface PatternDrawing {
  /** A CSS `background` value: a flat colour or a `linear-gradient`. */
  base: string;
  /** Plain-coloured shapes over it, positioned in percentages of the frame. */
  layers?: React.CSSProperties[];
  /** Drawn as SVG, for what CSS cannot carry into a browser export. */
  svg?: (id: string) => { defs?: React.ReactNode; shapes: React.ReactNode };
}

/**
 * A radial gradient with separate horizontal and vertical radii.
 *
 * SVG's `<radialGradient>` only has `r`, and in `objectBoundingBox` units that
 * one radius already stretches with the box — `r = 0.8` is 80% of the width
 * *and* 80% of the height. Squashing it to `ry` is a scale about the centre,
 * which is what CSS's `ellipse <rx> <ry>` means.
 */
function ellipseGradient(cx: number, cy: number, rx: number, ry: number) {
  return {
    cx: String(cx),
    cy: String(cy),
    r: String(rx),
    gradientTransform: `translate(${cx} ${cy}) scale(1 ${ry / rx}) translate(${-cx} ${-cy})`,
  };
}

const PATTERNS: Record<BackgroundPattern, (from: string, to: string) => PatternDrawing> = {
  solid: (_from, to) => ({ base: to }),
  linear: (from, to) => ({ base: `linear-gradient(160deg, ${from}, ${to})` }),
  glow: (from, to) => ({
    // Was `radial-gradient(ellipse 80% 70% at 50% 15%, …)`, which the browser
    // exporter drew as nothing at all.
    base: to,
    svg: (id) => ({
      defs: (
        <radialGradient id={id} {...ellipseGradient(0.5, 0.15, 0.8, 0.7)}>
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={to} />
        </radialGradient>
      ),
      shapes: <rect width="100%" height="100%" fill={`url(#${id})`} />,
    }),
  }),
  corner: (from, to) => ({
    base: to,
    svg: (id) => ({
      defs: (
        <radialGradient id={id} {...ellipseGradient(0.12, 1, 0.9, 0.9)}>
          <stop offset="0" stopColor={from} />
          <stop offset="1" stopColor={to} />
        </radialGradient>
      ),
      shapes: <rect width="100%" height="100%" fill={`url(#${id})`} />,
    }),
  }),
  base: (from, to) => ({ base: `linear-gradient(to bottom, ${from} 0 72%, ${to} 72% 100%)`, layers: [] }),
  panel: (from, to) => ({ base: `linear-gradient(to right, ${from} 0 66%, ${to} 66% 100%)`, layers: [] }),
  band: (from, to) => ({
    base: `linear-gradient(to bottom, ${from} 0 38%, ${to} 38% 62%, ${from} 62% 100%)`,
    layers: [],
  }),
  arc: (from, to) => ({
    base: to,
    layers: [
      {
        left: "-25%",
        right: "-25%",
        top: "52%",
        bottom: "-85%",
        borderRadius: "50%",
        backgroundColor: from,
        opacity: 0.5,
      },
    ],
  }),
  swell: (from, to) => ({
    base: to,
    layers: [
      {
        left: "-35%",
        right: "-35%",
        top: "64%",
        bottom: "-120%",
        borderRadius: "50%",
        backgroundColor: from,
        opacity: 0.32,
      },
      {
        left: "-35%",
        right: "-35%",
        top: "78%",
        bottom: "-120%",
        borderRadius: "50%",
        backgroundColor: from,
        opacity: 0.42,
      },
    ],
  }),
  curve: (from, to) => ({
    base: to,
    layers: [
      {
        left: "30%",
        right: "-60%",
        top: "-60%",
        bottom: "-10%",
        borderRadius: "50%",
        backgroundColor: from,
        opacity: 0.42,
      },
    ],
  }),
  hatch: (from, to) => ({
    // Was `repeating-linear-gradient`, whose repeat the exporter lost: it finds
    // "linear-gradient(" *inside* the word and draws one stretched gradient
    // across the whole frame.
    base: to,
    svg: (id) => ({
      defs: (
        <pattern id={id} width="16" height="16" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
          <rect width="6" height="16" fill={from} />
        </pattern>
      ),
      shapes: <rect width="100%" height="100%" fill={`url(#${id})`} opacity={0.3} />,
    }),
  }),
  dots: (from, to) => ({
    base: to,
    svg: (id) => ({
      defs: (
        <pattern id={id} width="18" height="18" patternUnits="userSpaceOnUse">
          <circle cx="9" cy="9" r="1.8" fill={from} />
        </pattern>
      ),
      shapes: <rect width="100%" height="100%" fill={`url(#${id})`} opacity={0.42} />,
    }),
  }),
  grid: (from, to) => ({
    base: to,
    svg: (id) => ({
      defs: (
        <pattern id={id} width="22" height="22" patternUnits="userSpaceOnUse">
          <rect width="1" height="22" fill={from} />
          <rect width="22" height="1" fill={from} />
        </pattern>
      ),
      shapes: <rect width="100%" height="100%" fill={`url(#${id})`} opacity={0.3} />,
    }),
  }),
};

/**
 * The SVG the drawn parts live in.
 *
 * `width` and `height` are the element's own pixel size when the caller knows
 * it, and that is load-bearing rather than tidy: the browser exporter
 * serialises this element and loads it through `new Image()`, and an SVG
 * carrying only percentages has no intrinsic size, so Chrome would rasterise
 * it at its 300×150 default and the exporter would letterbox that into the
 * frame. Given the real numbers, the two agree and it is drawn edge to edge.
 *
 * The editor's thumbnails pass nothing and fall back to percentages, which is
 * correct there — nothing rasterises them, the DOM just lays them out.
 */
const DrawnLayer: React.FC<{
  draw: (id: string) => { defs?: React.ReactNode; shapes: React.ReactNode };
  size?: { width: number; height: number };
}> = ({ draw, size }) => {
  // Unique per instance: the head, body and end backgrounds are three of these
  // in one document, and a repeated `<defs>` id means all three take the first
  // one's gradient. The colons React puts in its ids are legal in the DOM but
  // not worth carrying into a serialised `url(#…)`.
  const id = `veyso-bg-${useId().replace(/:/g, "")}`;
  const { defs, shapes } = draw(id);

  return (
    <svg
      width={size?.width ?? "100%"}
      height={size?.height ?? "100%"}
      viewBox={size ? `0 0 ${size.width} ${size.height}` : undefined}
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0, display: "block" }}
    >
      {defs ? <defs>{defs}</defs> : null}
      {shapes}
    </svg>
  );
};

/**
 * One patterned background, at whatever size its parent is.
 *
 * Positioned rather than an `AbsoluteFill` so the editor can drop the same
 * thing into a 140px thumbnail; the parent supplies the box.
 */
export const PatternLayer: React.FC<{
  pattern: BackgroundPattern;
  from: string;
  to: string;
  size?: { width: number; height: number };
}> = ({ pattern, from, to, size }) => {
  const { base, layers = [], svg } = PATTERNS[pattern](from, to);
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", background: base }}>
      {layers.map((layer, index) => (
        <div key={index} style={{ position: "absolute", ...layer }} />
      ))}
      {svg ? <DrawnLayer draw={svg} size={size} /> : null}
    </div>
  );
};

/**
 * One mesh, at whatever size its parent is.
 *
 * Positioned rather than an `AbsoluteFill` for the same reason `PatternLayer`
 * is: the editor drops this into a 140px thumbnail, and the thumbnail has to
 * be drawn by the thing that draws the render or it is a promise nobody keeps.
 *
 * Each field is its own radial gradient over the ground, drawn in SVG because
 * CSS radial gradients do not survive a browser export — a mesh came out as
 * the flat ground colour with every field missing.
 *
 * The radii are CSS's `farthest-corner`, which is what a `radial-gradient`
 * with no size given means: the ellipse keeps the farthest-side proportions
 * and passes through the farthest corner, hence the √2.
 */
export const MeshLayer: React.FC<{
  layout: MeshLayout;
  ground: string;
  fields: readonly string[];
  size?: { width: number; height: number };
}> = ({ layout, ground, fields, size }) => {
  const blobs = meshFields(layout, fields);

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", backgroundColor: ground }}>
      <DrawnLayer
        size={size}
        draw={(id) => ({
          defs: blobs.map((blob, index) => {
            const x = blob.x / 100;
            const y = blob.y / 100;
            const rx = Math.max(x, 1 - x) * Math.SQRT2;
            const ry = Math.max(y, 1 - y) * Math.SQRT2;
            return (
              <radialGradient key={index} id={`${id}-${index}`} {...ellipseGradient(x, y, rx, ry)}>
                <stop offset="0" stopColor={blob.colour} stopOpacity={1} />
                {/* Faded to the same colour rather than to nothing: a stop that
                    fades towards transparent black turns the edge muddy. */}
                <stop offset={blob.reach / 100} stopColor={blob.colour} stopOpacity={0} />
              </radialGradient>
            );
          }),
          shapes: blobs.map((_blob, index) => (
            <rect key={index} width="100%" height="100%" fill={`url(#${id}-${index})`} />
          )),
        })}
      />
    </div>
  );
};

export const BackgroundLayer: React.FC<{ background: Background }> = ({ background }) => {
  const assetUrl = useAssetUrl();
  // An `AbsoluteFill` is exactly the composition, so this is the pixel size of
  // the element the SVG has to fill. See `DrawnLayer` for why it matters.
  const { width, height } = useVideoConfig();
  const frame = { width, height };

  if (background.kind === "color") {
    return <AbsoluteFill style={{ backgroundColor: background.color }} />;
  }

  if (background.kind === "gradient") {
    return (
      <AbsoluteFill
        style={{ backgroundImage: `linear-gradient(${background.angle}deg, ${background.from}, ${background.to})` }}
      />
    );
  }

  if (background.kind === "mesh") {
    return (
      <AbsoluteFill>
        <MeshLayer layout={background.layout} ground={background.ground} fields={background.fields} size={frame} />
      </AbsoluteFill>
    );
  }

  if (background.kind === "pattern") {
    return (
      <AbsoluteFill>
        <PatternLayer pattern={background.pattern} from={background.from} to={background.to} size={frame} />
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill style={{ backgroundColor: "#000" }}>
      <Img src={assetUrl(background.assetKey)!} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <AbsoluteFill style={{ backgroundColor: `rgba(0,0,0,${background.dim})` }} />
    </AbsoluteFill>
  );
};
